import type { DaySessions, SymbolSessions } from "./types";

const DAY_LABELS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

export function getDayLabel(dayIndex: number): string {
  return DAY_LABELS[dayIndex] ?? `Day ${dayIndex}`;
}

export function formatMinutesAsTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
}

export function formatSessionWindow(startMinutes: number, endMinutes: number): string {
  return `${formatMinutesAsTime(startMinutes)} – ${formatMinutesAsTime(endMinutes)}`;
}

export function formatDaySessions(daySessions: DaySessions | undefined | null): string {
  if (!daySessions?.windows?.length) return "—";

  return daySessions.windows
    .map((window) => formatSessionWindow(window.startMinutes, window.endMinutes))
    .join(", ");
}

/** Broker server clock derived from UTC + server timezone offset (matches MT5 ServerTime). */
export function getBrokerDate(timeZoneMinutes: number, nowMs = Date.now()): Date {
  return new Date(nowMs + timeZoneMinutes * 60_000);
}

function isSessionActiveForDay(
  daySessions: DaySessions[] | undefined,
  dayOfWeek: number,
  minutesOfDay: number,
): boolean {
  const windows = daySessions?.[dayOfWeek]?.windows ?? [];
  return windows.some(
    (window) =>
      minutesOfDay > window.startMinutes && minutesOfDay < window.endMinutes,
  );
}

export function isQuoteSessionActive(
  sessions: SymbolSessions | null | undefined,
  timeZoneMinutes: number,
  nowMs = Date.now(),
): boolean {
  if (!sessions?.quoteSessions?.length) return false;

  const brokerDate = getBrokerDate(timeZoneMinutes, nowMs);
  const dayOfWeek = brokerDate.getUTCDay();
  const minutesOfDay = brokerDate.getUTCHours() * 60 + brokerDate.getUTCMinutes();

  return isSessionActiveForDay(sessions.quoteSessions, dayOfWeek, minutesOfDay);
}

export function isTradeSessionActive(
  sessions: SymbolSessions | null | undefined,
  timeZoneMinutes: number,
  nowMs = Date.now(),
): boolean {
  if (!sessions?.tradeSessions?.length) return false;

  const brokerDate = getBrokerDate(timeZoneMinutes, nowMs);
  const dayOfWeek = brokerDate.getUTCDay();
  const minutesOfDay = brokerDate.getUTCHours() * 60 + brokerDate.getUTCMinutes();

  return isSessionActiveForDay(sessions.tradeSessions, dayOfWeek, minutesOfDay);
}

export function formatBrokerDateTime(
  timeZoneMinutes: number,
  nowMs = Date.now(),
): string {
  return getBrokerDate(timeZoneMinutes, nowMs).toISOString().replace("T", " ").slice(0, 19);
}
